/* SpawnAnything — "Armeen" window: which side fights with what and how many. Top: "Eigene Figur erstellen" (the AI
   prompt + photo, moved in from the panel) with "In Sammlung speichern". Middle: Blau | Rot, each with a big rotating
   model of its unit and the existing army controls (#army0 / #army1: unit, count, weapon, size, mount, formation …).
   Bottom: every unit as a 3D card ("Meine Sammlung" first); a click gives it to the chosen side.
   The army controls keep their ids and listeners (main.js readCfg/writeCfg, campaign, multiplayer); this module only
   re-parents them and drives the selects. */
import './armyWindow.css';
import { createWindow, card } from './window.js';
import { getPreview3D } from './preview3d.js';
import * as THREE from 'three';
import { unitObject, recipeObject, humanRef } from './models3d.js';
import { formationLayout } from '../sim/features/formation.js';
import { collection } from '../game/collection.js';

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmt = n => Number(n || 0).toLocaleString('de-DE');

/**
 * deps: { types: () => TYPES, recipes: () => RECIPES, weapons: () => WEAPONS, teams: TEAMS, locked: key => bool,
 *         readCfg: t => cfg, summary: () => text, toast, colKey: id => typeKey (registered collection unit),
 *         onCollectionAdd: entry => typeKey, onCollectionRemove: entry => void }
 */
export function createArmyWindow(deps) {
  const pv = getPreview3D();
  const W = createWindow({ id: 'armyWin', title: 'Armeen', sub: 'Wer kämpft gegen wen – und wie viele', icon: 'ti-swords', wide: true,
    foot: 'Klick auf eine Figur gibt sie der gewählten Seite. Gesperrte Figuren gibt es in Kisten.' });

  /* ---- top: own figure ---- */
  const gen = document.createElement('section'); gen.className = 'aw-win-sec army-gen';
  gen.innerHTML = `<h3><i class="ti ti-sparkles" aria-hidden="true"></i>Eigene Figur erstellen</h3><div class="army-gen-row"></div>
    <div class="army-gen-save" hidden><span class="army-gen-name"></span><button class="ghost" type="button" data-save>In Sammlung speichern</button></div>`;
  W.body.appendChild(gen);
  const genRow = gen.querySelector('.army-gen-row');
  // the AI prompt block of the panel: input, "Für Blau / Für Rot", quota line, photo button
  const promptBlock = document.getElementById('prompt')?.closest('#panel > div');
  if (promptBlock) { promptBlock.querySelector('label.h')?.remove(); promptBlock.querySelector('.seg')?.remove(); genRow.appendChild(promptBlock); }
  let lastGen = null;
  const saveRow = gen.querySelector('.army-gen-save');
  saveRow.querySelector('[data-save]').addEventListener('click', () => {
    if (!lastGen) return;
    const e = collection.add({ kind: 'unit', name: lastGen.recipe.name || lastGen.prompt, prompt: lastGen.prompt, recipe: lastGen.recipe });
    if (!e) { deps.toast?.('Speichern hat nicht geklappt (Speicher voll?)'); return; }
    deps.onCollectionAdd?.(e);
    deps.toast?.(`${e.name} ist jetzt in deiner Sammlung`);
    saveRow.hidden = true; lastGen = null; renderGrid();
  });

  /* ---- middle: the two sides ---- */
  const sides = document.createElement('section'); sides.className = 'army-sides';
  W.body.appendChild(sides);
  const side = [0, 1].map(t => {
    const col = document.createElement('div'); col.className = 'army-side t' + t;
    col.innerHTML = `<div class="army-hero"><span class="army-hero-view"></span><span class="army-hero-n"></span><button type="button" class="army-human" aria-pressed="false" title="Einen 1,8 m großen Menschen zum Größenvergleich zeigen"><i class="ti ti-user" aria-hidden="true"></i> Mensch</button></div>`;
    const sec = document.getElementById('army' + t);
    if (sec) col.appendChild(sec);
    sides.appendChild(col);
    if (t === 0) { const vs = document.createElement('div'); vs.className = 'army-vs hd'; vs.textContent = 'VS'; sides.appendChild(vs); }
    return { col, view: col.querySelector('.army-hero-view'), n: col.querySelector('.army-hero-n'), key: '' };
  });
  document.querySelector('#panel > .vs')?.remove();
  // the hero shows the ARMY: up to CAP figures in the chosen formation (the sim's own layout), true size, body sliders,
  // optionally a 1.8 m person for scale; the box grows with big figures instead of clipping them
  const CAP = 80;
  let human = true;
  try { human = localStorage.getItem('aw-hero-human') !== '0'; } catch (e) { human = true; } // on by default: size needs a reference
  const heroMake = t => () => {
    const c = deps.readCfg(t), T = deps.types(), ty = T[c.type] || {};
    const one = unitObject(c.type, t, T, c.weapon, deps.recipes(), { t: c.t, width: c.width, head: c.head });
    const size = c.size || 1, r = (ty.r || 0.5) * size, n = Math.max(1, Math.min(c.count, CAP));
    const X = new Float32Array(n), Z = new Float32Array(n), ROT = new Float32Array(n);
    let seed = 12345; const R = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    formationLayout({ ext: [0, 0] }, deps.formation ? deps.formation(t) : 0, { count: n, r, start: 0 }, t, 0, R, X, Z, ROT, 1e4);
    const g = new THREE.Group(); let maxX = 0;
    for (let i = 0; i < n; i++) {
      const m = i ? one.clone() : one; m.scale.setScalar(size);
      m.position.set(Z[i], 0, t === 0 ? X[i] - X[0] : X[0] - X[i]); g.add(m);
      if (Z[i] > maxX) maxX = Z[i];
    }
    if (human) { const h = humanRef(); h.position.set(maxX + r * 2 + 0.8, 0, 0); g.add(h); }
    return g;
  };
  const heroKey = t => { const c = deps.readCfg(t); return [c.type, c.weapon, Math.min(c.count, CAP), c.size, c.t, c.width, c.head, deps.formation ? deps.formation(t) : 0, human].join('|'); };
  const heroH = t => { const c = deps.readCfg(t), many = Math.min(c.count, CAP) > 1; return Math.round(Math.min(380, 180 + 34 * Math.log2(c.size || 1) + (many ? 30 : 0))); };
  function syncHero(t) {
    const c = deps.readCfg(t), key = heroKey(t);
    side[t].view.style.height = heroH(t) + 'px';
    side[t].n.textContent = `${fmt(c.count)} × ${deps.types()[c.type]?.name || c.type}`;
    if (key === side[t].key) return;
    side[t].key = key;
    pv.add(side[t].view, heroMake(t), { spin: 0.5, rot: t ? 0.5 : -0.5, clip: heroClip() });
  }

  /* ---- bottom: unit grid ---- */
  const pick = document.createElement('section'); pick.className = 'aw-win-sec army-pick';
  pick.innerHTML = `<h3><i class="ti ti-users" aria-hidden="true"></i>Figuren<span class="seg army-for" role="group" aria-label="Für welche Seite"><button type="button" class="tb0" data-for="0" aria-pressed="true">Für Blau</button><button type="button" class="tb1" data-for="1" aria-pressed="false">Für Rot</button></span></h3><div class="army-grids"></div>`;
  W.body.appendChild(pick);
  let forT = 0;
  pick.querySelectorAll('[data-for]').forEach(b => b.addEventListener('click', () => { forT = +b.dataset.for; pick.querySelectorAll('[data-for]').forEach(o => o.setAttribute('aria-pressed', String(o === b))); markPicked(); }));
  const grids = pick.querySelector('.army-grids');

  function give(typeKey) {
    const sel = document.getElementById('type' + forT); if (!sel) return;
    if (![...sel.options].some(o => o.value === typeKey)) { deps.toast?.('Diese Figur ist hier nicht verfügbar'); return; }
    sel.value = typeKey; sel.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => { syncAll(); markPicked(); }, 0);
  }
  function section(title, icon, entries) {
    if (!entries.length) return;
    const sec = document.createElement('div'); sec.className = 'army-grp';
    sec.innerHTML = `<h4><i class="ti ${icon}" aria-hidden="true"></i>${esc(title)} <small>${entries.length}</small></h4><div class="aw-grid"></div>`;
    const g = sec.querySelector('.aw-grid');
    for (const e of entries) {
      const ty = deps.types()[e.key]; if (!ty) continue;
      const c = card({ title: ty.name || e.key, text: e.text || `LP ${fmt(ty.hp)} · ${deps.weapons()[ty.weapon]?.name || ''}`, tag: e.tag || '', locked: deps.locked(e.key) });
      c.dataset.type = e.key;
      c.addEventListener('click', () => give(e.key));
      if (e.entry) {
        const x = document.createElement('span'); x.className = 'army-del'; x.title = 'Aus der Sammlung entfernen'; x.textContent = '✕'; x.setAttribute('role', 'button');
        x.addEventListener('click', ev => { ev.stopPropagation(); if (!confirm(`„${e.entry.name}“ aus der Sammlung entfernen?`)) return; collection.remove(e.entry.id); deps.onCollectionRemove?.(e.entry); renderGrid(); });
        c.appendChild(x);
      }
      g.appendChild(c);
      pv.add(c.querySelector('.aw-card-view'), () => (e.entry ? recipeObject(deps.recipes()[ty.recipe] || e.entry.recipe, forT) : unitObject(e.key, 0, deps.types(), null, deps.recipes())), { clip: W.body });
    }
    grids.appendChild(sec);
  }
  function renderGrid() {
    pv.removeWithin(grids); grids.innerHTML = '';
    const T = deps.types();
    const mine = collection.list('unit').map(entry => ({ key: deps.colKey(entry.id), entry, tag: 'Sammlung' })).filter(e => T[e.key]);
    const ai = Object.keys(T).filter(k => k.startsWith('u_gen')).map(key => ({ key, tag: 'KI' }));
    const base = Object.keys(T).filter(k => !k.startsWith('u_')).map(key => ({ key }));
    section('Meine Sammlung', 'ti-bookmark', mine);
    section('Gerade erschaffen', 'ti-sparkles', ai);
    section('Alle Figuren', 'ti-users', base.filter(e => !deps.locked(e.key)));
    section('Aus Kisten', 'ti-box', base.filter(e => deps.locked(e.key)));
    markPicked();
  }
  function markPicked() {
    const k = [deps.readCfg(0).type, deps.readCfg(1).type];
    grids.querySelectorAll('.aw-card').forEach(c => {
      c.classList.toggle('pick0', c.dataset.type === k[0]); c.classList.toggle('pick1', c.dataset.type === k[1]);
      c.setAttribute('aria-pressed', String(c.dataset.type === k[forT]));
    });
  }
  function syncAll() { syncHero(0); syncHero(1); }

  // the sides (big models + controls) can also live in the main menu's configurator (attachSides); clip to that host
  let host = null;
  const heroClip = () => host ? null : W.body;
  W.onOpen(() => { if (host) detachSides(); renderGrid(); side[0].key = side[1].key = ''; syncAll(); });
  function attachSides(el) { host = el; el.appendChild(sides); side[0].key = side[1].key = ''; syncAll(); pv.wake(); }
  function detachSides() { if (!host) return; host = null; gen.after(sides); side[0].key = side[1].key = ''; pv.remove(side[0].view); pv.remove(side[1].view); }
  W.onClose(() => { pv.removeWithin(grids); deps.onClose?.(); });
  // keep the heroes in step with the controls
  sides.addEventListener('change', () => setTimeout(() => { syncAll(); markPicked(); }, 0));
  let liveT = 0;
  sides.addEventListener('input', () => { clearTimeout(liveT); liveT = setTimeout(() => { syncAll(); }, 140); }); // sliders: live, throttled
  sides.querySelectorAll('.army-human').forEach(b => b.addEventListener('click', () => {
    human = !human; try { localStorage.setItem('aw-hero-human', human ? '1' : '0'); } catch (e) { /* private mode */ }
    sides.querySelectorAll('.army-human').forEach(o => o.setAttribute('aria-pressed', String(human))); syncAll();
  }));
  sides.querySelectorAll('.army-human').forEach(o => o.setAttribute('aria-pressed', String(human)));

  return {
    open: W.open, close: W.close, win: W, attachSides, detachSides,
    /** main.js: a figure was generated for side t (offer saving it) */
    generated(t, recipe, prompt) {
      lastGen = { recipe, prompt };
      if (collection.has('unit', recipe)) { saveRow.hidden = true; return; }
      saveRow.hidden = false; saveRow.querySelector('.army-gen-name').textContent = `„${recipe.name || prompt}“ für ${t ? 'Rot' : 'Blau'} erschaffen.`;
      if (W.isOpen) { renderGrid(); syncAll(); }
    },
    refresh() { if (W.isOpen) { renderGrid(); syncAll(); } },
  };
}
